import { action, computed, makeAutoObservable, observable, runInAction } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { ToasterState } from 'src/AppState/ToasterState';
import { ComplianceAutomationType } from 'src/api_openapi/v2/compliance/automationSchemas';
import { apiGetComplianceAutomationRequest } from 'src/api_openapi/v2/compliance/apiGetComplianceAutomation';
import { apiUpdateComplianceAutomationRequest } from 'src/api_openapi/v2/compliance/apiUpdateComplianceAutomation';
import { apiCreateComplianceAutomationRequest } from 'src/api_openapi/v2/compliance/apiCreateComplianceAutomation';
import { apiTriggerComplianceAutomationRequest } from 'src/api_openapi/v2/compliance/apiTriggerComplianceAutomation';
import { apiGetSourceOfFundsConfigRequest } from 'src/api_openapi/v2/source-of-funds/apiGetSourceOfFundsConfig';
import { apiSetSourceOfFundsConfigRequest } from 'src/api_openapi/v2/source-of-funds/apiSetSourceOfFundsConfig';
import { apiDeleteSourceOfFundsConfigRequest } from 'src/api_openapi/v2/source-of-funds/apiDeleteSourceOfFundsConfig';
import { SourceOfFundsConfigurationType } from 'src/api_openapi/v2/source-of-funds/sourceOfFundsSchemas';
import {
    buildSourceOfFundsAutomationDtos,
    buildSourceOfFundsConfigDto,
    hydrateSourceOfFundsStateFromAutomations,
    hydrateSourceOfFundsStateFromConfig,
    SOURCE_OF_FUNDS_NEW_ACCOUNT_AUTOMATION_NAME,
    SOURCE_OF_FUNDS_THRESHOLD_AUTOMATION_NAME,
} from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFundsAutomation.mapper';
import {
    SourceOfFundsActionSelectionType,
    SourceOfFundsCalculationType,
    SourceOfFundsCalendarPeriodValueType,
    SourceOfFundsPeriodType,
} from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFunds.types';
import {
    DEFAULT_SOURCE_OF_FUNDS_CALENDAR_PERIOD_VALUE,
    DEFAULT_SOURCE_OF_FUNDS_COMPLIANCE_RESTRICTION_PERIOD,
    DEFAULT_SOURCE_OF_FUNDS_NEW_ACCOUNT_AMOUNT,
    DEFAULT_SOURCE_OF_FUNDS_NEW_ACCOUNT_DAYS_SINCE_SIGNUP,
    DEFAULT_SOURCE_OF_FUNDS_NEW_ACCOUNT_DEPOSIT_WINDOW_DAYS,
    DEFAULT_SOURCE_OF_FUNDS_ROLLING_PERIOD_DAYS,
    DEFAULT_SOURCE_OF_FUNDS_THRESHOLD_AMOUNT,
} from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFundsThresholdOptions';

type AutomationFetchStateType =
    | { kind: 'idle' }
    | { kind: 'found'; data: ComplianceAutomationType }
    | { kind: 'missing' }
    | { kind: 'error'; message: string };

type ConfigFetchStateType =
    | { kind: 'idle' }
    | { kind: 'found'; data: SourceOfFundsConfigurationType }
    | { kind: 'missing' }
    | { kind: 'error'; message: string };

const validatePositiveIntegerString = (value: string): string | null => {
    const trimmed = value.trim();

    if (trimmed === '') {
        return 'This field is required.';
    }

    if (!/^\d+$/.test(trimmed) || parseInt(trimmed, 10) <= 0) {
        return 'Enter a whole number greater than 0.';
    }

    return null;
};

const applyActionSelection = (
    current: SourceOfFundsActionSelectionType,
    key: keyof SourceOfFundsActionSelectionType,
    value: boolean
): SourceOfFundsActionSelectionType => {
    return { ...current, [key]: value };
};

const toFetchState = (
    response:
        | { status: 200; body: ComplianceAutomationType }
        | { status: 404; body: unknown }
        | { status: 500; body: { error: { message: string } } }
): AutomationFetchStateType => {
    if (response.status === 200) {
        return { kind: 'found', data: response.body };
    }

    if (response.status === 404) {
        return { kind: 'missing' };
    }

    return {
        kind: 'error',
        message:
            response.body.error.message === ''
                ? 'Failed to load automation configuration. Saving is disabled until reload succeeds.'
                : response.body.error.message,
    };
};

const toConfigFetchState = (
    response:
        | { status: 200; body: SourceOfFundsConfigurationType }
        | { status: 404; body: unknown }
        | { status: 500; body: { error: { message: string } } }
): ConfigFetchStateType => {
    if (response.status === 200) {
        return { kind: 'found', data: response.body };
    }

    if (response.status === 404) {
        return { kind: 'missing' };
    }

    return {
        kind: 'error',
        message:
            response.body.error.message === ''
                ? 'Failed to load Source of Funds threshold configuration. Saving is disabled until reload succeeds.'
                : response.body.error.message,
    };
};

export class SourceOfFundsState {
    @observable public thresholdEnabled = true;
    @observable public thresholdAmount: string = DEFAULT_SOURCE_OF_FUNDS_THRESHOLD_AMOUNT;
    @observable public thresholdCalculationType: SourceOfFundsCalculationType = 'net';
    @observable public thresholdPeriodType: SourceOfFundsPeriodType = 'rolling';
    @observable public thresholdRollingPeriodDays: string = DEFAULT_SOURCE_OF_FUNDS_ROLLING_PERIOD_DAYS;
    @observable public thresholdCalendarPeriodValue: SourceOfFundsCalendarPeriodValueType =
        DEFAULT_SOURCE_OF_FUNDS_CALENDAR_PERIOD_VALUE;
    @observable public thresholdActions: SourceOfFundsActionSelectionType = {
        complianceRestriction: false,
        sendEmail: false,
        showPopup: false,
    };
    @observable public thresholdComplianceRestrictionPeriod: string =
        DEFAULT_SOURCE_OF_FUNDS_COMPLIANCE_RESTRICTION_PERIOD;

    @observable public newAccountEnabled = false;
    @observable public newAccountCalculationType: SourceOfFundsCalculationType = 'net';
    @observable public newAccountAmount: string = DEFAULT_SOURCE_OF_FUNDS_NEW_ACCOUNT_AMOUNT;
    @observable public newAccountDaysSinceSignup: string = DEFAULT_SOURCE_OF_FUNDS_NEW_ACCOUNT_DAYS_SINCE_SIGNUP;
    @observable public newAccountDepositWindowDays: string = DEFAULT_SOURCE_OF_FUNDS_NEW_ACCOUNT_DEPOSIT_WINDOW_DAYS;
    @observable public newAccountActions: SourceOfFundsActionSelectionType = {
        complianceRestriction: false,
        sendEmail: false,
        showPopup: false,
    };
    @observable public newAccountComplianceRestrictionPeriod: string =
        DEFAULT_SOURCE_OF_FUNDS_COMPLIANCE_RESTRICTION_PERIOD;

    @observable public isSaving = false;
    @observable public isLoading = true;
    @observable public testAccountId = '';
    @observable public isTriggering = false;
    @observable public triggerFeedbackMessage: string | null = null;
    @observable public triggerFeedbackVariant: 'default' | 'destructive' = 'default';

    @observable private thresholdFetchState: AutomationFetchStateType = { kind: 'idle' };
    @observable private newAccountFetchState: AutomationFetchStateType = { kind: 'idle' };
    @observable private configFetchState: ConfigFetchStateType = { kind: 'idle' };
    @observable private formErrorMessage: string | null = null;

    public constructor(private readonly appState: AppState) {
        makeAutoObservable(this);

        void this.loadConfiguration();
    }

    @computed public get errorMessage(): string | null {
        if (this.formErrorMessage !== null) {
            return this.formErrorMessage;
        }

        if (this.configFetchState.kind === 'error') {
            return this.configFetchState.message;
        }

        if (this.thresholdFetchState.kind === 'error') {
            return this.thresholdFetchState.message;
        }

        if (this.newAccountFetchState.kind === 'error') {
            return this.newAccountFetchState.message;
        }

        return null;
    }

    @computed public get isTestTriggerAvailable(): boolean {
        return !this.appState.isProdMode;
    }

    @computed public get thresholdAmountError(): string | null {
        if (!this.thresholdEnabled) {
            return null;
        }

        return validatePositiveIntegerString(this.thresholdAmount);
    }

    @computed public get thresholdRollingPeriodDaysError(): string | null {
        if (!this.thresholdEnabled || this.thresholdPeriodType !== 'rolling') {
            return null;
        }

        return validatePositiveIntegerString(this.thresholdRollingPeriodDays);
    }

    @computed public get newAccountAmountError(): string | null {
        if (!this.thresholdEnabled || !this.newAccountEnabled) {
            return null;
        }

        return validatePositiveIntegerString(this.newAccountAmount);
    }

    @computed public get newAccountDaysSinceSignupError(): string | null {
        if (!this.thresholdEnabled || !this.newAccountEnabled) {
            return null;
        }

        return validatePositiveIntegerString(this.newAccountDaysSinceSignup);
    }

    @computed public get newAccountDepositWindowDaysError(): string | null {
        if (!this.thresholdEnabled || !this.newAccountEnabled) {
            return null;
        }

        return validatePositiveIntegerString(this.newAccountDepositWindowDays);
    }

    @computed public get hasValidationErrors(): boolean {
        return (
            this.thresholdAmountError !== null ||
            this.thresholdRollingPeriodDaysError !== null ||
            this.newAccountAmountError !== null ||
            this.newAccountDaysSinceSignupError !== null ||
            this.newAccountDepositWindowDaysError !== null
        );
    }

    @computed private get thresholdAutomationExists(): boolean | null {
        switch (this.thresholdFetchState.kind) {
            case 'found':
                return true;
            case 'missing':
                return false;
            case 'error':
            case 'idle':
                return null;
        }
    }

    @computed private get parsedTestAccountId(): number | null {
        const parsed = parseInt(this.testAccountId.trim(), 10);

        if (isNaN(parsed) || parsed <= 0) {
            return null;
        }

        return parsed;
    }

    @computed public get canTriggerAutomation(): boolean {
        return this.thresholdAutomationExists === true && this.parsedTestAccountId !== null && !this.isTriggering;
    }

    @action public setThresholdEnabled = (value: boolean): void => {
        this.thresholdEnabled = value;
    };

    @action public setThresholdAmount = (value: string): void => {
        this.thresholdAmount = value;
    };

    @action public setThresholdCalculationType = (value: SourceOfFundsCalculationType): void => {
        this.thresholdCalculationType = value;
    };

    @action public setThresholdPeriodType = (value: SourceOfFundsPeriodType): void => {
        this.thresholdPeriodType = value;
    };

    @action public setThresholdRollingPeriodDays = (value: string): void => {
        this.thresholdRollingPeriodDays = value;
    };

    @action public setThresholdCalendarPeriodValue = (value: SourceOfFundsCalendarPeriodValueType): void => {
        this.thresholdCalendarPeriodValue = value;
    };

    @action public setThresholdAction = (key: keyof SourceOfFundsActionSelectionType, value: boolean): void => {
        this.thresholdActions = applyActionSelection(this.thresholdActions, key, value);
    };

    @action public clearThresholdActions = (): void => {
        this.thresholdActions = { complianceRestriction: false, sendEmail: false, showPopup: false };
    };

    @action public setThresholdComplianceRestrictionPeriod = (value: string): void => {
        this.thresholdComplianceRestrictionPeriod = value;
    };

    @action public setNewAccountEnabled = (value: boolean): void => {
        this.newAccountEnabled = value;
    };

    @action public setNewAccountCalculationType = (value: SourceOfFundsCalculationType): void => {
        this.newAccountCalculationType = value;
    };

    @action public setNewAccountAmount = (value: string): void => {
        this.newAccountAmount = value;
    };

    @action public setNewAccountDaysSinceSignup = (value: string): void => {
        this.newAccountDaysSinceSignup = value;
    };

    @action public setNewAccountDepositWindowDays = (value: string): void => {
        this.newAccountDepositWindowDays = value;
    };

    @action public setNewAccountAction = (key: keyof SourceOfFundsActionSelectionType, value: boolean): void => {
        this.newAccountActions = applyActionSelection(this.newAccountActions, key, value);
    };

    @action public clearNewAccountActions = (): void => {
        this.newAccountActions = { complianceRestriction: false, sendEmail: false, showPopup: false };
    };

    @action public setNewAccountComplianceRestrictionPeriod = (value: string): void => {
        this.newAccountComplianceRestrictionPeriod = value;
    };

    @action public setTestAccountId = (value: string): void => {
        this.testAccountId = value;
        this.triggerFeedbackMessage = null;
    };

    @action public loadConfiguration = async (options?: { silent?: boolean }): Promise<void> => {
        const silent = options?.silent === true;

        if (!silent) {
            this.isLoading = true;
        }
        this.formErrorMessage = null;

        const headers = { authorization: `Bearer ${this.appState.common.session.currentJwt}` };
        const gatewayUrl = this.appState.common.appEnvParams.gatewayUrl;

        try {
            const [configResponse, thresholdResponse, newAccountResponse] = await Promise.all([
                apiGetSourceOfFundsConfigRequest(gatewayUrl, headers),
                apiGetComplianceAutomationRequest(
                    gatewayUrl,
                    { name: SOURCE_OF_FUNDS_THRESHOLD_AUTOMATION_NAME },
                    headers
                ),
                apiGetComplianceAutomationRequest(
                    gatewayUrl,
                    { name: SOURCE_OF_FUNDS_NEW_ACCOUNT_AUTOMATION_NAME },
                    headers
                ),
            ]);

            runInAction(() => {
                this.configFetchState = toConfigFetchState(configResponse);
                this.thresholdFetchState = toFetchState(thresholdResponse);
                this.newAccountFetchState = toFetchState(newAccountResponse);
                this.thresholdEnabled = this.configFetchState.kind === 'found';

                if (this.configFetchState.kind === 'found') {
                    hydrateSourceOfFundsStateFromConfig(this, this.configFetchState.data);
                }

                hydrateSourceOfFundsStateFromAutomations(
                    this,
                    this.thresholdFetchState.kind === 'found' ? this.thresholdFetchState.data : null,
                    this.newAccountFetchState.kind === 'found' ? this.newAccountFetchState.data : null
                );
            });
        } catch {
            runInAction(() => {
                this.configFetchState = {
                    kind: 'error',
                    message: 'Failed to load Source of Funds configuration.',
                };
            });
        } finally {
            if (!silent) {
                runInAction(() => {
                    this.isLoading = false;
                });
            }
        }
    };

    @action public saveConfiguration = async (): Promise<void> => {
        if (this.hasValidationErrors) {
            this.formErrorMessage = 'Please fix the highlighted fields before saving.';
            return;
        }

        const headers = { authorization: `Bearer ${this.appState.common.session.currentJwt}` };
        const gatewayUrl = this.appState.common.appEnvParams.gatewayUrl;

        this.isSaving = true;
        this.formErrorMessage = null;

        try {
            const { threshold, newAccount } = buildSourceOfFundsAutomationDtos(this);
            const configDto = buildSourceOfFundsConfigDto(this);

            const saveOne = async (dto: ComplianceAutomationType, exists: boolean): Promise<'ok' | 'error'> => {
                const response = exists
                    ? await apiUpdateComplianceAutomationRequest(
                          gatewayUrl,
                          { name: dto.name, requestBody: dto },
                          headers
                      )
                    : await apiCreateComplianceAutomationRequest(gatewayUrl, { requestBody: dto }, headers);

                return response.status === 200 ? 'ok' : 'error';
            };

            const saveConfig = async (): Promise<'ok' | 'error'> => {
                if (this.thresholdEnabled) {
                    const response = await apiSetSourceOfFundsConfigRequest(
                        gatewayUrl,
                        { requestBody: configDto },
                        headers
                    );

                    return response.status === 200 ? 'ok' : 'error';
                }

                const response = await apiDeleteSourceOfFundsConfigRequest(gatewayUrl, headers);

                return response.status === 200 || response.status === 404 ? 'ok' : 'error';
            };

            const [configResult, thresholdResult, newAccountResult] = await Promise.all([
                saveConfig(),
                saveOne(threshold, this.thresholdFetchState.kind === 'found'),
                saveOne(newAccount, this.newAccountFetchState.kind === 'found'),
            ]);

            if (configResult === 'error' || thresholdResult === 'error' || newAccountResult === 'error') {
                this.formErrorMessage = 'Failed to save Source of Funds configuration.';
                return;
            }

            ToasterState.get(this.appState.common).setToast({
                variant: 'success',
                title: 'Success',
                message: 'Source of Funds automation configuration saved.',
            });
            await this.loadConfiguration({ silent: true });
        } catch {
            this.formErrorMessage = 'Failed to save Source of Funds automation configuration.';
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    @action public triggerAutomation = async (): Promise<void> => {
        const accountId = this.parsedTestAccountId;

        if (this.thresholdAutomationExists !== true) {
            this.triggerFeedbackMessage = 'Save the automation configuration before triggering.';
            this.triggerFeedbackVariant = 'destructive';
            return;
        }

        if (accountId === null) {
            this.triggerFeedbackMessage = 'Enter a valid account ID.';
            this.triggerFeedbackVariant = 'destructive';
            return;
        }

        this.isTriggering = true;
        this.triggerFeedbackMessage = null;

        try {
            const response = await apiTriggerComplianceAutomationRequest(
                this.appState.common.appEnvParams.gatewayUrl,
                {
                    requestBody: {
                        automation_name: SOURCE_OF_FUNDS_THRESHOLD_AUTOMATION_NAME,
                        config: {
                            universe: this.appState.common.appEnvParams.universe_api,
                            accountId,
                        },
                    },
                },
                { authorization: `Bearer ${this.appState.common.session.currentJwt}` }
            );

            if (response.status === 200) {
                this.triggerFeedbackMessage = `Automation triggered for account ${accountId}.`;
                this.triggerFeedbackVariant = 'default';
                return;
            }

            this.triggerFeedbackMessage = 'Failed to trigger Source of Funds automation.';
            this.triggerFeedbackVariant = 'destructive';
        } catch {
            this.triggerFeedbackMessage = 'Failed to trigger Source of Funds automation.';
            this.triggerFeedbackVariant = 'destructive';
        } finally {
            this.isTriggering = false;
        }
    };
}
